import { FiltersMap } from '@wix/bex-core';
import { action } from 'mobx';
import {
  DragAndDropBaseStateParams,
  DragAndDropState,
  DragItemOver,
} from './DragAndDropState';
import { NestedTableItem } from '../../state';

interface NestedDragAndDropStateParams<T, F extends FiltersMap>
  extends DragAndDropBaseStateParams<T, F> {}
export class NestedDragAndDropState<C extends string, T, F extends FiltersMap> {
  dnd: DragAndDropState<T, F>;
  parentCollection;

  constructor(params: NestedDragAndDropStateParams<T, F>) {
    const { collection } = params;
    this.parentCollection = collection;

    this.dnd = new DragAndDropState<T, F>({
      ...params,
      move: this._move.bind(this),
      announcements: {
        dragHandleLabel: (id) => {
          const { translate: t } = params.container;
          const item = collection.getKeyedItem(id as string)?.item;
          const itemName = item?.state.collection.itemName;

          return t('cairo.dragAndDrop.button-role-label.sr', {
            itemName:
              item && itemName ? itemName(item.originalKeyedItem?.item) : '',
          });
        },
        itemDescription: (id) => {
          const item = collection.getKeyedItem(id as string)?.item;
          const itemName = item?.state.collection.itemName;

          return item && itemName ? itemName(item.originalKeyedItem?.item) : '';
        },
      },
    });
  }

  _move(ev: { over: DragItemOver | null }) {
    const { parentCollection } = this;
    const { onSubmit } = this.dnd;

    const item: NestedTableItem<C, T, F> | undefined =
      parentCollection.getKeyedItem(ev.over?.id as string)?.item;

    if (!item) {
      return;
    }

    const itemCollection = item?.state.collection;
    const { optimisticActions } = itemCollection;

    if (optimisticActions == null) {
      return;
    }

    optimisticActions._orderByMode = 'moves';

    const dragEndEvent = this.dnd._createDragEndEvent(ev);

    if (dragEndEvent == null) {
      return;
    }

    optimisticActions.move(
      {
        ...dragEndEvent,
        after:
          dragEndEvent.after &&
          item?.state.collection.getKeyedItem(dragEndEvent.after.key)
            ? dragEndEvent.after
            : null,
        query: itemCollection.originQuery,
        filtersKeyHash: itemCollection.query.filtersKeyHash,
      },
      {
        submit: onSubmit
          ? (event) => onSubmit(this.dnd._createSimpleDragEndEvent(event))
          : undefined,
        collectionSnapshot: item?.state.getCollectionSnapshot() ?? {},
      },
    );
  }

  init(params: { a11yContainer?: HTMLElement | null | undefined }) {
    const disposers = [this.dnd.init(params)];

    return action(() => {
      disposers.forEach((d) => d());
    });
  }
}
